/**
 * Detects a running release_pipeline job for the current org.
 * Used to reconnect the SSE stream and restore progress after a page refresh.
 *
 * Mount via SalesforceReleaseLayout — it only needs to run when the user is on
 * the release page. refetchOnWindowFocus ensures a fresh check each time the
 * user returns to the tab.
 */
import { useQuery } from "@tanstack/react-query";
import { useOrg } from "@/hooks/org/useOrg";
import { useAuthStoreV1 } from "@/stores/authStore_v1";
import { getOngoingReleaseJob, type OngoingReleaseJob } from "@/services/salesforce-release";

export const ongoingReleaseJobKeys = {
  all: ["ongoingReleaseJob"] as const,
  detail: (orgId: string) => [...ongoingReleaseJobKeys.all, "detail", orgId] as const,
};

export function useOngoingReleaseJob() {
  const { selectedOrg } = useOrg();
  const orgId = selectedOrg?.id ?? null;
  const userId = useAuthStoreV1((s) => s.user?.id ?? null);

  return useQuery<OngoingReleaseJob | null>({
    queryKey: ongoingReleaseJobKeys.detail(orgId ?? ""),
    queryFn: () => getOngoingReleaseJob(orgId!, userId),
    enabled: Boolean(orgId),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: true,
  });
}
